import I18n from 'i18n-js';
import gql from 'graphql-tag';
import React, { useEffect } from 'react';
import { ChildProps } from 'react-apollo';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { formatExperimentsForEventAttributes } from '@reverbdotcom/commons/src/elog/mparticle';
import experiments from '@reverbdotcom/commons/src/experiments';
import { formatAmount } from '@reverbdotcom/commons/src/money';
import { RCLoadingBars } from '@reverbdotcom/cadence/components';
import { withUserContext, IUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import { convertMoney } from '@reverbdotcom/commons/src/convert_currency';
import PriceDisplay from '@reverbdotcom/commons/src/components/price_display';
import PriceDisplayRange from '@reverbdotcom/commons/src/components/price_display_range';
import { conditionUuidForSlug, NON_FUNCTIONING, BRAND_NEW, B_STOCK } from '@reverbdotcom/commons/src/condition_helpers';
import { I18N } from '@reverbdotcom/commons/src/components/translate';
import { CoreRecommendedPriceRange } from '../../../../../schemas/rql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import PriceGuideGraphIcon from '../../../../../../images/price-guide-tool/colored-price-guide-graph.svg';
import SellFormPricingGuidanceModal from './SellFormPricingGuidanceModal';
import { IShopConfig } from '../../SellFormContext';
import { isShopInUSA } from '../sections/PricingSection';

interface IProps extends IUserContext {
  shopConfig: IShopConfig;
  conditionSlug?: string;
  canonicalProduct?: {
    title: string;
    id: string;
  };
}

export function sanitizePriceRecommendations({
  priceRecommendations,
  currency,
  exchangeRates,
}) {
  const { priceLow, priceMiddle, priceHigh } = priceRecommendations[0] ?? {};

  if (!priceLow || !priceMiddle || !priceHigh) return {};

  let exchangedLow;
  let exchangedMiddle;
  let exchangedHigh;

  try {
    exchangedLow = convertMoney(priceLow, currency, exchangeRates);
    exchangedMiddle = convertMoney(priceMiddle, currency, exchangeRates);
    exchangedHigh = convertMoney(priceHigh, currency, exchangeRates);
  } catch {
    return {};
  }

  const displayLow = formatAmount(exchangedLow, { precision: 0 });
  const displayMiddle = formatAmount(exchangedMiddle, { precision: 0 });
  const displayHigh = formatAmount(exchangedHigh, { precision: 0 });

  const formattedPriceLow = {
    amountCents: exchangedLow.amountCents,
    currency: exchangedLow.currency,
    display: displayLow,
  };

  const formattedPriceMiddle = {
    amountCents: exchangedMiddle.amountCents,
    currency: exchangedMiddle.currency,
    display: displayMiddle,
  };

  const formattedPriceHigh = {
    amountCents: exchangedHigh.amountCents,
    currency: exchangedHigh.currency,
    display: displayHigh,
  };

  return {
    formattedPriceLow,
    formattedPriceMiddle,
    formattedPriceHigh,
  };
}

export function isConditionIneligibleForEstimates(conditionSlug) {
  return !conditionSlug || [BRAND_NEW, NON_FUNCTIONING, B_STOCK].includes(conditionSlug);
}

function shouldSkipQuery({
  canonicalProduct,
  shopConfig,
  conditionSlug,
}) {
  return !canonicalProduct?.id || !isShopInUSA(shopConfig) || isConditionIneligibleForEstimates(conditionSlug);
}

export function SellFormPricingGuidance({
  data,
  canonicalProduct,
  conditionSlug,
  user,
}: ChildProps<IProps, CoreRecommendedPriceRange.Query>) {

  const {
    me,
    exchangeRates,
    priceRecommendations,
    loading,
  } = data ?? {};

  const rates = exchangeRates?.rates ?? [];
  const recommendations = priceRecommendations?.priceRecommendations ?? [];
  const currency = me?.shop?.currency || user.currency;

  const { formattedPriceLow, formattedPriceMiddle, formattedPriceHigh } = sanitizePriceRecommendations({
    priceRecommendations: recommendations,
    exchangeRates: rates,
    currency,
  });

  const hasNoEstimate = !formattedPriceLow || !formattedPriceMiddle || !formattedPriceHigh;
  const hasDataServicesPriceEstimate = !hasNoEstimate;

  useEffect(() => {
    if (!loading) {
      trackEvent({
        eventName: MParticleEventName.ComponentView,
        componentName: 'SellFormPricingGuidance',
        hasDataServicesPriceEstimate,
        cpId: canonicalProduct?.id,
        conditionSlug,
        experiments: formatExperimentsForEventAttributes(user, [
          experiments.SELL_FORM_PRICING_GUIDANCE,
        ]),
      });
    }
  }, [loading]);

  if (loading) {
    return (
      <div className="d-flex fx-justify-center ptb-4">
        <RCLoadingBars />
      </div>
    );
  }

  return (
    <div className="sell-form-pricing-guidance">
      {hasNoEstimate ? (
        <div className="sell-form-pricing-guidance__no-estimate">
          <p>
            {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.competitivePriceSellsFaster')}
          </p>
        </div>
      ) : (
        <div>
          <div className="sell-form-pricing-guidance__header">
            <h3>
              {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.title')}
            </h3>
          </div>

          <div className="sell-form-pricing-guidance__body">
            <div className="sell-form-pricing-guidance__body__competitive-pricing">
              <div className="sell-form-pricing-guidance__body__competitive-pricing__price">
                <img
                  src={PriceGuideGraphIcon}
                  alt={I18n.t('discovery.sellForm.pricingSection.pricingGuidance.graphIcon')}
                />

                <div>
                  <h4>
                    {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.competitivePrice')}
                  </h4>

                  <PriceDisplay
                    amountCents={formattedPriceMiddle.amountCents}
                    currency={formattedPriceMiddle.currency}
                    display={formattedPriceMiddle.display}
                  />
                </div>
              </div>

              <div className="sell-form-pricing-guidance__body__competitive-pricing__description">
                <I18N
                  html
                  text="discovery.sellForm.pricingSection.pricingGuidance.description"
                  args={{
                    modelName: canonicalProduct.title,
                    conditionName: I18n.t(`discovery.conditions.${conditionSlug}`),
                  }}
                />
              </div>
            </div>

            <div className="sell-form-pricing-guidance__body__estimated-price-range-section">
              <h4>
                {I18n.t('discovery.sellForm.pricingSection.pricingGuidance.estimatedPriceRange')}
              </h4>

              <div>
                <PriceDisplayRange
                  moneyLow={formattedPriceLow}
                  moneyHigh={formattedPriceHigh}
                  displayConfig={{ precision: 0 }}
                />
              </div>

              <div>
                <SellFormPricingGuidanceModal
                  canonicalProductIds={[canonicalProduct.id]}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const withQuery = withGraphql<IProps>(
  gql`
    query DataServices_SellForm__PricingRecommendations(
      $priceRecommendationQueries: [Input_reverb_pricing_PriceRecommendationQuery]
      $loggedOut: Boolean!
    ) {
      priceRecommendations(input: {priceRecommendationQueries: $priceRecommendationQueries}) {
        priceRecommendations {
          priceLow {
            amountCents
            amount
            currency
          }
          priceMiddle {
            amountCents
            amount
            currency
          }
          priceHigh {
            amountCents
            amount
            currency
          }
        }
      }
      me @skip(if: $loggedOut) {
        uuid
        shop {
          _id
          currency
        }
      }
      exchangeRates(input: { inverseReverseRates: true }) {
        rates {
          from
          to
          rate
        }
      }
    }
  `,
  {
    skip: ({ shopConfig, canonicalProduct, conditionSlug }) => shouldSkipQuery({
      canonicalProduct,
      shopConfig,
      conditionSlug,
    }),
    options: ({ conditionSlug, canonicalProduct, user }) => {
      const canonicalProductId = canonicalProduct.id;
      const conditionUuids = [conditionUuidForSlug(conditionSlug)];

      const priceRecommendationQueries = conditionUuids.filter(Boolean).map(conditionUuid => ({
        canonicalProductId,
        conditionUuid,
      }));

      return {
        ssr: false,
        fetchPolicy: 'network-only',
        variables: {
          loggedOut: user.loggedOut,
          priceRecommendationQueries,
        },
      };
    },
  },
);

export default withUserContext(withQuery(SellFormPricingGuidance));
